import { Space, Spin } from 'antd';
import { Checkbox, Form, Input, InputNumber, Modal, Select } from '@maxtropy/components';
import { FC, useEffect, useMemo, useState } from 'react';
import {
  BindDeviceFormSubmitProps,
  MonitorDeviceManageType,
  MonitorDeviceManageTypeDisplay,
} from '../MonitorDeviceManage';
import { useRequest } from 'ahooks';
import { Button } from '@maxtropy/components';
import {
  apiV2SupervisoryAccountDeviceDetailPost,
  apiV2SupervisoryAccountDeviceSceneTargetInOu2Post,
  apiV2SupervisoryAccountDeviceVerifyDeviceUrlPost,
  apiV2SupervisoryAccountDeviceVerifyDeviceWeightingPost,
  apiV2OuOuListPost,
  V2OuOuListPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { AccessMode } from '@/pages/VideoMonitoring/type';
import { ToolbarControlDisplay } from '@/pages/VideoMonitoring/components/HKVideoPlayer';
import { ipAddressValidator } from '@/shared/components/IpAddress';

export interface IBindOrEditMonitorDeviceFormModalProps {
  deviceTabActiveKey: string;
  monitorDeviceId?: number;
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: BindDeviceFormSubmitProps) => Promise<void>;
  submitBtnLoading: boolean;
  accessMode?: AccessMode;
}

export const configTypeOptions = [
  {
    label: '原生播放',
    value: 1,
  },
  {
    label: '代理播放',
    value: 2,
  },
];

export const channelIndexOptions = [
  {
    label: 1,
    value: 0,
  },
  {
    label: 2,
    value: 1,
  },
];
const rtspResolutionOptions = [
  {
    label: '720P',
    value: 1,
  },
  {
    label: '1080P',
    value: 2,
  },
  {
    label: '4K',
    value: 3,
  },
];

const BindOrEditMonitorDeviceFormModal: FC<IBindOrEditMonitorDeviceFormModalProps> = props => {
  const { monitorDeviceId, visible, onCancel, onSubmit, deviceTabActiveKey, submitBtnLoading, accessMode } = props;
  const [form] = Form.useForm();
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [ou, setOu] = useState<V2OuOuListPostResponse['list']>();

  useEffect(() => {
    apiV2OuOuListPost({}).then(res => setOu(res.list || []));
  }, []);

  const ouOptions = useMemo(() => {
    if (ou?.length !== 0) {
      return ou?.map(item => ({ label: `${item.name}-${item.serialNumber}-${item.tenantName}`, value: item.id }));
    }
  }, [ou]);

  useEffect(() => {
    if (monitorDeviceId) {
      setDataLoading(true);
      apiV2SupervisoryAccountDeviceDetailPost({
        id: monitorDeviceId,
      }).then(res => {
        if (res) {
          setDataLoading(false);
          form.setFieldsValue({
            ...res,
            supervisoryTarget:
              res.supervisoryType === MonitorDeviceManageType.OTHERS
                ? res.supervisoryTarget
                : Number(res.supervisoryTarget),
          });
        }
      });
    }
  }, [monitorDeviceId, form]);

  const { data: monitorTargetList } = useRequest(
    async () => {
      const res = await apiV2SupervisoryAccountDeviceSceneTargetInOu2Post({
        id: Number(deviceTabActiveKey),
      });
      return res ? res.list : [];
    },
    {
      ready: !!deviceTabActiveKey && visible,
      refreshDeps: [deviceTabActiveKey, visible],
    }
  );

  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const onOK = (values: BindDeviceFormSubmitProps) => {
    onSubmit(values);
  };

  // 是否有ou下拉列表
  const hasOuSelectOptions = useMemo(() => {
    const deviceActiveKey = Number(deviceTabActiveKey);
    return (
      deviceActiveKey === MonitorDeviceManageType.PV ||
      deviceActiveKey === MonitorDeviceManageType.BSA ||
      deviceActiveKey === MonitorDeviceManageType.NONELECENERGYMED
    );
  }, [deviceTabActiveKey]);

  // 监控目标下拉列表
  const targetOptions = useMemo(() => {
    if (monitorTargetList && monitorTargetList.length !== 0) {
      return monitorTargetList.map(i => ({
        label: deviceTabActiveKey === '4' ? `${i.supervisoryTargetName}-${i.tenantInfoName}` : i.supervisoryTargetName,
        value: i.supervisoryTargetId ?? i.supervisoryTargetName,
      }));
    }
  }, [monitorTargetList, deviceTabActiveKey]);

  const checkUniqueWeight = async (rule: any, value: number) => {
    if (value) {
      const res = await apiV2SupervisoryAccountDeviceVerifyDeviceWeightingPost({
        weighting: value,
        id: monitorDeviceId ?? undefined,
        supervisoryType: Number(deviceTabActiveKey),
      });
      if (!res.flag) {
        return Promise.reject('权重已被使用！');
      } else {
        return Promise.resolve();
      }
    }
  };
  const checkUniqueUrl = async (rule: any, value: string) => {
    if (value) {
      if (!/^((ezopen:)\/\/)(.{1,}\/)(.{1,}\/)(.{1,})$/.test(value)) {
        return Promise.reject(new Error('请输入正确URL'));
      }

      const res = await apiV2SupervisoryAccountDeviceVerifyDeviceUrlPost({
        supervisoryUrl: value,
        id: monitorDeviceId ?? undefined,
      });
      if (!res.flag) {
        return Promise.reject('监控地址(URL)-预览地址已被使用！');
      } else {
        return Promise.resolve();
      }
    }
  };
  const checkRtspUrl = async (_: any, value: string) => {
    if (value) {
      if (
        !/^rtsp:\/\/[^:\s]+:[^@\s]+@((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d):([1-9]\d{0,4})\/.+$/i.test(
          value
        )
      ) {
        return Promise.reject(new Error('请输入正确格式 rtsp://[user:pass@]ip:port/path'));
      }
      const [, portStr] = value.match(/:(\d{1,5})\//) ?? [];
      const portNum = Number(portStr);
      if (!portNum || portNum < 1 || portNum > 65535) {
        return Promise.reject(new Error('RTSP流地址端口范围为1-65535'));
      }
      return Promise.resolve();
    }
  };

  return (
    <Modal
      contentClassName="modal-form-content"
      size="large"
      destroyOnClose
      open={visible}
      onCancel={onCancel}
      title={
        monitorDeviceId
          ? `编辑${MonitorDeviceManageTypeDisplay[deviceTabActiveKey as unknown as MonitorDeviceManageType]}监控设备`
          : `绑定${MonitorDeviceManageTypeDisplay[deviceTabActiveKey as unknown as MonitorDeviceManageType]}监控设备`
      }
      okText="保存"
      footer={
        <Space size={8}>
          <Button onClick={onCancel}>取消</Button>
          <Button
            type="primary"
            loading={submitBtnLoading}
            onClick={() => {
              form.submit();
            }}
          >
            保存
          </Button>
        </Space>
      }
    >
      <Spin spinning={dataLoading}>
        <Form onFinish={onOK} form={form} initialValues={{ configType: 1, channelIndex: 0 }}>
          {(accessMode === AccessMode.YSY || accessMode === AccessMode.RTSP) && (
            <Form.Item
              name="supervisoryDeviceName"
              label="监控设备名称"
              rules={[
                { required: true, message: '请输入' },
                { max: 50, message: '最多输入五十个字符' },
              ]}
            >
              <Input placeholder="请输入" />
            </Form.Item>
          )}
          <Form.Item
            name="supervisoryTarget"
            label="监控目标"
            rules={
              Number(deviceTabActiveKey) === MonitorDeviceManageType.OTHERS
                ? [
                    {
                      required: true,
                      message: '请输入',
                    },
                    { max: 20, message: '最多输入二十个字符' },
                  ]
                : [
                    {
                      required: true,
                      message: '请选择',
                    },
                  ]
            }
          >
            {Number(deviceTabActiveKey) === MonitorDeviceManageType.OTHERS ? (
              <Input placeholder="请输入" />
            ) : (
              <Select placeholder="请选择" options={targetOptions} showSearch optionFilterProp="label" />
            )}
          </Form.Item>
          {!hasOuSelectOptions && (
            <Form.Item name="ouId" label="运营单元" rules={[{ required: true, message: '请选择' }]}>
              <Select placeholder="请选择" options={ouOptions} showSearch optionFilterProp="label" />
            </Form.Item>
          )}

          <Form.Item
            name="weighting"
            label="权重"
            validateTrigger={['onBlur']}
            rules={[
              { required: true, message: '请输入权重' },
              { validator: (rule: any, value: number) => checkUniqueWeight(rule, value) },
            ]}
          >
            <InputNumber style={{ width: '100%' }} min={1} step={1} precision={0} placeholder="请输入" />
          </Form.Item>

          <Form.Item
            name="configType"
            label="配置类型"
            validateTrigger={['onBlur']}
            rules={[{ required: true, message: '请输入配置类型' }]}
            hidden={accessMode === AccessMode.RTSP}
          >
            <Select style={{ width: '100%' }} placeholder="请输入" options={configTypeOptions} />
          </Form.Item>
          {accessMode === AccessMode.YSY && (
            <Form.Item
              name="supervisoryUrl"
              label="监控地址(URL)-预览地址"
              validateTrigger={['onBlur']}
              rules={[
                { required: true, message: '请输入监控地址(URL)-预览地址' },
                { validator: (rule: any, value: string) => checkUniqueUrl(rule, value) },
              ]}
              extra={'格式如下：ezopen://[验证码@]域名/序列号/通道号[.清晰度].live[?参数1=值&参数2=值]'}
            >
              <Input placeholder="请输入" />
            </Form.Item>
          )}
          {accessMode === AccessMode.HK && (
            <>
              <Form.Item
                name="supervisoryDeviceName"
                label="监控设备名称"
                rules={[
                  { required: true, message: '请输入' },
                  { max: 20, message: '最多输入20个字符' },
                ]}
              >
                <Input placeholder="请输入" />
              </Form.Item>
              <Form.Item
                name="monitorLocation"
                label="监控位置"
                rules={[
                  { required: true, message: '请输入' },
                  { max: 50, message: '最多输入50个字符' },
                ]}
              >
                <Input placeholder="请输入" />
              </Form.Item>
              <Form.Item
                name="cameraUsername"
                label="用户名"
                rules={[
                  { required: true, message: '请输入' },
                  { max: 50, message: '最多输入50个字符' },
                ]}
              >
                <Input placeholder="请输入" />
              </Form.Item>
              <Form.Item
                name="cameraPassword"
                label="密码"
                rules={[
                  { required: true, message: '请输入' },
                  { max: 50, message: '最多输入50个字符' },
                ]}
              >
                <Input.Password placeholder="请输入" />
              </Form.Item>
              <Form.Item
                name="ipAddress"
                label="IP地址"
                rules={[{ required: true, message: '请输入' }, { validator: ipAddressValidator }]}
              >
                <Input placeholder="请输入" />
              </Form.Item>
              <Form.Item name="port" label="端口号" rules={[{ required: true, message: '请输入' }]}>
                <InputNumber placeholder="请输入" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="channelIndex" label="频道序号" rules={[{ required: true, message: '请输入' }]}>
                <Select style={{ width: '100%' }} placeholder="请输入" options={channelIndexOptions} />
              </Form.Item>
              <Form.Item name="cameraPort" label="设备端口">
                <InputNumber placeholder="请输入" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="rtspPort" label="RTSP端口" rules={[{ required: true, message: '请输入' }]}>
                <InputNumber placeholder="请输入" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="enableFeatureList" label="启用功能" rules={[{ required: true, message: '请输入' }]}>
                <Checkbox.Group>
                  {Object.entries(ToolbarControlDisplay).map(([key, label]) => {
                    return (
                      key !== '0' && (
                        <Checkbox key={key} value={key}>
                          {label}
                        </Checkbox>
                      )
                    );
                  })}
                </Checkbox.Group>
              </Form.Item>
            </>
          )}
          {accessMode === AccessMode.RTSP && (
            <>
              <Form.Item
                name="rtspAddress"
                label="RTSP流地址"
                validateTrigger={['onBlur']}
                rules={[
                  { required: true, message: '请输入RTSP流地址' },
                  { validator: (rule: any, value: string) => checkRtspUrl(rule, value) },
                ]}
                extra="格式如下：rtsp://user:pass@ip:port/path"
              >
                <Input placeholder="请输入" />
              </Form.Item>
              <Form.Item name="resolutionType" label="分辨率" rules={[{ required: true, message: '请选择' }]}>
                <Select style={{ width: '100%' }} placeholder="请选择" options={rtspResolutionOptions} />
              </Form.Item>
              <Form.Item name="bitrate" label="码率设置" rules={[{ required: true, message: '请输入' }]}>
                <InputNumber placeholder="请输入" style={{ width: '100%' }} min={0.01} addonAfter="Mbps" />
              </Form.Item>
            </>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default BindOrEditMonitorDeviceFormModal;
